import React from 'react';
import { motion, useInView } from 'framer-motion';
import { Phone, MessageCircle } from 'lucide-react';
import { socialLinks } from '../mock';
import { useLanguage } from '../context/LanguageContext';

const QuickContact = () => {
  const { language } = useLanguage();
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  const texts = {
    it: {
      title: 'Hai una domanda?',
      subtitle: 'Contattaci subito per informazioni e disponibilità',
      callLabel: 'Chiama ora',
      whatsappLabel: 'Scrivi su WhatsApp',
    },
    en: {
      title: 'Have a question?',
      subtitle: 'Contact us now for information and availability',
      callLabel: 'Call now',
      whatsappLabel: 'Message on WhatsApp',
    },
    de: {
      title: 'Haben Sie eine Frage?',
      subtitle: 'Kontaktieren Sie uns jetzt für Informationen und Verfügbarkeit',
      callLabel: 'Jetzt anrufen',
      whatsappLabel: 'Nachricht auf WhatsApp',
    },
    fr: {
      title: 'Une question ?',
      subtitle: 'Contactez-nous maintenant pour informations et disponibilités',
      callLabel: 'Appelez maintenant',
      whatsappLabel: 'Écrire sur WhatsApp',
    },
    es: {
      title: '¿Alguna pregunta?',
      subtitle: 'Contáctanos ahora para información y disponibilidad',
      callLabel: 'Llamar ahora',
      whatsappLabel: 'Escribir por WhatsApp',
    },
  };

  const t = texts[language] || texts.it;

  return (
    <section ref={ref} className="relative bg-[#F5F1E8] py-16 overflow-hidden">
      {/* Decorative gradient divider */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#C9826B] to-transparent" />

      <div className="max-w-5xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="text-center mb-10"
        >
          <h3 className="text-3xl md:text-4xl font-serif text-[#2A2520] mb-3">
            {t.title}
          </h3>
          <p className="text-[#2A2520]/70 text-base md:text-lg">
            {t.subtitle}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-4 md:gap-6 max-w-3xl mx-auto">
          {/* Phone Button */}
          <motion.a
            href={socialLinks.phoneLink}
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            whileHover={{ scale: 1.02, y: -4 }}
            whileTap={{ scale: 0.98 }}
            className="group flex items-center gap-4 bg-[#2A2520] hover:bg-[#C9826B] text-[#F5F1E8] px-6 py-5 shadow-lg transition-all duration-300"
            data-testid="quick-phone-link"
          >
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#D4AF37]/20 group-hover:bg-[#F5F1E8]/20 flex items-center justify-center transition-colors duration-300">
              <Phone className="w-5 h-5 text-[#D4AF37] group-hover:text-[#F5F1E8] transition-colors duration-300" />
            </div>
            <div className="flex-1 min-w-0 text-left">
              <p className="text-xs tracking-widest uppercase text-[#D4AF37] group-hover:text-[#F5F1E8]/90 transition-colors duration-300 mb-1">
                {t.callLabel}
              </p>
              <p className="text-lg md:text-xl font-light tracking-wide truncate">
                {socialLinks.phone}
              </p>
            </div>
          </motion.a>

          {/* WhatsApp Button */}
          <motion.a
            href={socialLinks.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            whileHover={{ scale: 1.02, y: -4 }}
            whileTap={{ scale: 0.98 }}
            className="group flex items-center gap-4 bg-[#25D366] hover:bg-[#1EBE57] text-white px-6 py-5 shadow-lg transition-all duration-300"
            data-testid="quick-whatsapp-link"
          >
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-white" fill="white" />
            </div>
            <div className="flex-1 min-w-0 text-left">
              <p className="text-xs tracking-widest uppercase text-white/80 mb-1">
                WhatsApp
              </p>
              <p className="text-lg md:text-xl font-light tracking-wide">
                {t.whatsappLabel}
              </p>
            </div>
          </motion.a>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#C9826B] to-transparent" />
    </section>
  );
};

export default QuickContact;
